import { useCallback } from 'react'

import Text from '@/elements/Text'
import Flex from '@/elements/Flex'
import Button from '@/elements/Button'
import FormToggle from '@/elements/FormToggle'
import { removeIdentity, useGetMyIdentities, useGetMyMFA, setMyMFA } from '@/api/user'
import { useAuthIdentities } from '@/hooks/useAuthIdentities'
import useBreakpoints from '@/hooks/useBreakpoints'
import toast from '@/utils/toast'

import EmailPasswordForm from './EmailPasswordForm'

const IDENTITY_PROVIDER_BY_KEY = {
  google: 'Google',
  apple: 'SignInWithApple',
  cognito: 'Cognito',
} as const

function AuthenticationDetails() {
  const { isMobile } = useBreakpoints()
  const { data: identitiesData, refetch: refetchIdentities } = useGetMyIdentities()
  const { data: mfaData, refetch: refetchMFA } = useGetMyMFA()
  const isMFAEnabled = mfaData?.data?.enabled || false

  const {
    identities,
    hasEmailPasswordIdentity,
    isEmailPasswordPrimary,
  } = useAuthIdentities(identitiesData?.data?.identities)

  const disconnectIdentity = useCallback(async (identityKey: string) => {
    const provider = IDENTITY_PROVIDER_BY_KEY[identityKey as keyof typeof IDENTITY_PROVIDER_BY_KEY]
    if (!provider) {
      toast.error('Unknown identity provider')
      return
    }

    try {
      await removeIdentity(provider)
      toast.success('Disconnected identity successfully')
      refetchIdentities()
    } catch {
      toast.error('Failed to disconnect identity')
    }
  }, [refetchIdentities])

  const enableMFA = useCallback(async (enabled: boolean) => {
    try {
      await setMyMFA(enabled)
      toast.success(`Multi-Factor Authentication ${enabled ? 'enabled' : 'disabled'} successfully`)
      refetchMFA()
    } catch {
      toast.error(`Failed to ${enabled ? 'enable' : 'disable'} Multi-Factor Authentication`)
    }
  }, [refetchMFA])

  return (
    <Flex wfull gap={3}>
      {/* Social Providers */}
      {identities.map(identity => (
        <Flex
          key={identity.key}
          inline={!isMobile}
          wfull
          wrap
          justify='space-between'
          align='center'
          style={{ minHeight: 54 }}
          gap={2}
        >
          <Flex wfull={isMobile} gap={1}>
            <Text variant='h4' color='black'>
              {identity.label}
            </Text>
            <Text variant='textblock' color='black-light-40'>
              {identity.connected
                ? identity.primary ? 'Primary' : 'Connected'
                : identity.description}
            </Text>
          </Flex>

          {identity.connected ? (
            <Button
              variant='danger'
              onClick={() => disconnectIdentity(identity.key)}
              disabled={identity.primary}
              fullWidth={isMobile}
              styles={{ minWidth: '130px' }}
            >
              Disconnect
            </Button>
          ) : (
            identity.startAuth && (
              <Button
                variant='secondary'
                disabled={identity.connected}
                onClick={identity.startAuth}
                fullWidth={isMobile}
                styles={{ minWidth: '130px' }}
              >
                Connect
              </Button>
            )
          )}
        </Flex>
      ))}

      <Flex color='gray-light-20' wfull style={{ height: '1px' }} />

      {/* Email & Password */}
      <EmailPasswordForm
        refetchIdentities={refetchIdentities}
        disconnect={disconnectIdentity}
        hasEmailPasswordIdentity={hasEmailPasswordIdentity}
        isPrimary={isEmailPasswordPrimary}
      />

      {/* Multi-Factor Authentication */}
      <Flex inline wfull justify='space-between' align='center' style={{ minHeight: 54 }} gap={2}>
        <Flex gap={1} style={{ flex: 1 }}>
          <Text variant='h4' color='black'>Multi-Factor Authentication</Text>
          <Text variant='textblock' color='black-light-40'>
            Add an extra layer of security by requiring a verification code on login.
          </Text>
        </Flex>
        <FormToggle
          name='mfa-toggle'
          value={isMFAEnabled}
          onChange={e => enableMFA(e.target.checked)}
          disabled={!hasEmailPasswordIdentity}
        />
      </Flex>
    </Flex>
  )
}

export default AuthenticationDetails
